import React from 'react';
import { ClockCircleOutlined } from '@ant-design/icons';
import { Empty } from 'antd';
import styled from 'styled-components';
import { useTimerStatuses } from '../../../../core/modules/pipelines/hooks';
import { TimerDetailSkeleton } from '../skeletons';
import { ITimerStatus } from '../../../../core/modules/pipelines/pipelines.model';
import { formatRelative, formatTimestamp } from '../../utils/formatStage';
import { BRENDA_GREEN } from '../../utils/statusColors';

const TimerCard = styled.div`
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 28px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
`;

const TimerTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.2px;
`;

const TimerSubtitle = styled.p`
  margin: 0 0 20px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.55);
`;

const TimerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

const TimerStat = styled.div`
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 14px 16px;
`;

const TimerStatLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 6px;
`;

const TimerStatValue = styled.div`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.85);
`;

interface ITimerDetailProps {
  pipelineId: string;
}

const TimerDetail: React.FC<ITimerDetailProps> = ({ pipelineId }) => {
  const { data, isLoading, isError } = useTimerStatuses();

  if (isLoading) {
    return <TimerDetailSkeleton />;
  }

  if (isError || !data) {
    return <Empty description='Failed to load timer status' />;
  }

  const timer: ITimerStatus | undefined = data.data.timers.find((t) => t.id === pipelineId);

  if (!timer) {
    return <Empty description='Timer not found' />;
  }

  return (
    <TimerCard>
      <TimerTitle>{timer.name}</TimerTitle>
      <TimerSubtitle>
        <ClockCircleOutlined style={{ marginRight: 6 }} />
        {timer.schedule}
      </TimerSubtitle>
      <TimerGrid>
        <TimerStat>
          <TimerStatLabel>Cron</TimerStatLabel>
          <TimerStatValue style={{ fontFamily: 'monospace' }}>
            {timer.scheduleCron ?? '—'}
          </TimerStatValue>
        </TimerStat>
        <TimerStat>
          <TimerStatLabel>Health</TimerStatLabel>
          <TimerStatValue style={{
            color: timer.healthy ? BRENDA_GREEN : '#cf1322', fontWeight: 600
          }}>
            {timer.healthy ? 'Healthy' : 'At risk'}
          </TimerStatValue>
        </TimerStat>
        <TimerStat>
          <TimerStatLabel>Next expected</TimerStatLabel>
          <TimerStatValue>
            {timer.nextExpectedAt
              ? `${formatTimestamp(timer.nextExpectedAt)} (${formatRelative(timer.nextExpectedAt)})`
              : '—'}
          </TimerStatValue>
        </TimerStat>
        <TimerStat>
          <TimerStatLabel>Last observed</TimerStatLabel>
          <TimerStatValue>
            {timer.lastObservedAt ? formatTimestamp(timer.lastObservedAt) : '—'}
          </TimerStatValue>
        </TimerStat>
      </TimerGrid>
    </TimerCard>
  );
};

export default TimerDetail;
